import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Stack,
  Chip,
  IconButton,
  Skeleton,
  Tooltip as MuiTooltip,
  Avatar
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  InfoOutlined,
  BarChart as BarChartIcon,
  TableChart,
  Download,
  Timeline,
  FilterAlt,
  DateRange,
  ArrowUpward,
  ArrowDownward,
  AttachMoney,
  ErrorOutline
} from "@mui/icons-material";
import axios from "axios";

const CollectionEfficiencyRatio = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");
  
  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingSampleData, setIsUsingSampleData] = useState(false);
  const [viewMode, setViewMode] = useState("chart"); // 'chart' or 'table'
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 12)),
    end: new Date()
  });
  const [totalCollected, setTotalCollected] = useState(0);
  const [totalDue, setTotalDue] = useState(0);
  const [efficiencyRatio, setEfficiencyRatio] = useState(0);
  const [monthlyGrowth, setMonthlyGrowth] = useState(0);
  const [prevData, setPrevData] = useState(null);

  // Configuration constants
  const CONFIG = {
    colors: {
      primary: [
        colors.primary[500],
        colors.blueAccent[500],
        colors.greenAccent[500],
        colors.redAccent[500]
      ],
      cardBg: theme.palette.background.default,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary
    }
  };

  // Generate sample data for demo purposes
  const generateSampleData = () => {
    const months = [];
    const now = new Date();
    let totalCollected = 0;
    let totalDue = 0;
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const collected = Math.floor(Math.random() * 50000) + 10000;
      const due = collected + Math.floor(Math.random() * 20000);
      const ratio = (collected / due) * 100;
      
      months.push({
        name: `${monthName} '${year}`,
        collected,
        due,
        ratio: parseFloat(ratio.toFixed(2)),
        month: date.getMonth() + 1,
        year: date.getFullYear()
      });
      
      totalCollected += collected;
      totalDue += due;
    }
    
    const efficiencyRatio = (totalCollected / totalDue) * 100;
    const growth = months.length > 1 ? 
      ((months[11].ratio - months[10].ratio) / months[10].ratio) * 100 : 0;
    
    return {
      data: months,
      totalCollected: totalCollected,
      totalDue: totalDue,
      efficiencyRatio: parseFloat(efficiencyRatio.toFixed(2)),
      growth: parseFloat(growth.toFixed(2))
    };
  };

  // Calculate statistics from data
  const calculateStats = (data) => {
    if (data.length === 0) return { 
      totalCollected: 0, 
      totalDue: 0, 
      efficiencyRatio: 0, 
      growth: 0 
    };
    
    const totalCollected = data.reduce((sum, item) => sum + (item.collected || 0), 0);
    const totalDue = data.reduce((sum, item) => sum + (item.due || 0), 0);
    
    // Calculate ratio with 3 decimal places then truncate to 2 for total
    const rawRatio = totalDue !== 0 ? (totalCollected / totalDue) * 100 : 0;
    const efficiencyRatio = Math.floor(rawRatio * 100) / 100; // Truncate to 0.02 instead of rounding
    
    const growth = data.length > 1 ? 
      ((data[data.length - 1].ratio - data[data.length - 2].ratio) / 
       (data[data.length - 2].ratio || 1)) * 100 : 0;    
       return {
        totalCollected: totalCollected,
        totalDue: totalDue,
        efficiencyRatio: efficiencyRatio,
        growth: parseFloat(growth.toFixed(2))
      };  };

  // Calculate change percentage
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  // Handle time range change
  const handleTimeRangeChange = (range) => {
    const now = new Date();
    let startDate = new Date();
    
    switch (range) {
      case '3months':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case '6months':
        startDate.setMonth(now.getMonth() - 6);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case '2years':
        startDate.setFullYear(now.getFullYear() - 2);
        break;
      default:
        startDate.setFullYear(now.getFullYear() - 1);
    }
    
    setDateRange({
      start: startDate,
      end: now
    });
  };

  // Fetch data from API
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsUsingSampleData(false);
      setPrevData(data);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/collectionEfficiencyRatio`,
        { cmpid },
        { headers: { Authorization: token } }
      );

      // Log the API response to console
      // console.log("API Response Coll:", response.data);

      // Check if API response is empty or has no data
      if (!response.data || Object.keys(response.data).length === 0) {
        // API returned empty data, use sample data
        setError("No data available. Showing sample data.");
        const sampleData = generateSampleData();
        setData(sampleData.data);
        setTotalCollected(sampleData.totalCollected);
        setTotalDue(sampleData.totalDue);
        setEfficiencyRatio(sampleData.efficiencyRatio);
        setMonthlyGrowth(sampleData.growth);
        setIsUsingSampleData(true);
        setLoading(false);
        return;
      }

      const formattedData = Object.values(response.data).map(item => {
        const dateObj = new Date(item.month + "-01");
        const monthName = dateObj.toLocaleString('default', { month: 'short' });
        const year = dateObj.getFullYear().toString().slice(-2);
        const collected = parseFloat(item.amount_collected) || 0;
        const due = parseFloat(item.amount_due) || 0;
        // Calculate ratio if not provided or invalid
        const ratio = item.ratio && !isNaN(item.ratio) ? parseFloat(item.ratio) : 
                      (due !== 0 ? (collected / due) * 100 : 0);
        
        return {
          name: `${monthName} '${year}`,
          collected,
          due,
          ratio,
          month: dateObj.getMonth() + 1,
          year: dateObj.getFullYear()
        };
      });

      const stats = calculateStats(formattedData);
      setData(formattedData);
      setTotalCollected(stats.totalCollected);
      setTotalDue(stats.totalDue);
      setEfficiencyRatio(stats.efficiencyRatio);
      setMonthlyGrowth(stats.growth);

    } catch (err) {
      console.error("Error fetching collection efficiency data:", err);
      setError("Failed to load data. Showing sample data.");
      const sampleData = generateSampleData();
      setData(sampleData.data);
      setTotalCollected(sampleData.totalCollected);
      setTotalDue(sampleData.totalDue);
      setEfficiencyRatio(sampleData.efficiencyRatio);
      setMonthlyGrowth(sampleData.growth);
      setIsUsingSampleData(true);
    } finally {
      setLoading(false);
    }
  };

  // Load sample data
  const loadSampleData = () => {
    setLoading(true);
    setError("Using sample data for demonstration purposes");
    setIsUsingSampleData(true);
    
    // Store current data as previous before loading new data
    setPrevData(data);
    
    const sampleData = generateSampleData();
    setData(sampleData.data);
    setTotalCollected(sampleData.totalCollected);
    setTotalDue(sampleData.totalDue);
    setEfficiencyRatio(sampleData.efficiencyRatio);
    setMonthlyGrowth(sampleData.growth);
    setLoading(false);
  };

  // Toggle between views
  const toggleViewMode = () => {
    setViewMode(viewMode === "chart" ? "table" : "chart");
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Export data to CSV
  const exportToCSV = () => {
    const headers = ["Month", "Amount Collected", "Amount Due", "Efficiency Ratio (%)"];
    const rows = data.map(item => [
      item.name,
      item.collected,
      item.due,
      item.ratio
    ]);
    
    let csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n" 
      + rows.map(row => row.join(",")).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "collection_efficiency_ratio.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataItem = payload[0].payload;
      
      return (
        <Card sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[3],
          borderRadius: 2,
          p: 2,
          minWidth: 180,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {dataItem.name}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Collected:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                Rs.{dataItem.collected.toLocaleString()}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Due:
              </Typography>
              <Typography variant="body2">
                Rs.{dataItem.due.toLocaleString()}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Ratio:
              </Typography>
              <Typography variant="body2" sx={{ 
                color: dataItem.ratio > 80 ? colors.greenAccent[500] : 
                       dataItem.ratio > 60 ? colors.blueAccent[500] : colors.redAccent[500],
                fontWeight: 600
              }}>
                {dataItem.ratio.toFixed(2)}%
              </Typography>
            </Box>
          </Stack>
        </Card>
      );
    }
    return null;
  };

  // Effect to fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Calculate changes from previous data
  const collectedChange = prevData ? calculateChange(
    totalCollected,
    calculateStats(prevData).totalCollected
  ) : 0;

  const dueChange = prevData ? calculateChange(
    totalDue,
    calculateStats(prevData).totalDue
  ) : 0;

  const ratioChange = prevData ? calculateChange(
    efficiencyRatio,
    calculateStats(prevData).efficiencyRatio
  ) : 0;

  const growthChange = prevData ? calculateChange(
    monthlyGrowth,
    calculateStats(prevData).growth
  ) : 0;

  return (
    <Card sx={{ 
      height: "100%",
      minHeight: 450,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[2],
      borderRadius: "12px",
      overflow: "hidden",
      border: `1px solid ${theme.palette.divider}`,
      display: "flex",
      flexDirection: "column",
      transition: "all 0.3s ease",
      '&:hover': {
        boxShadow: theme.shadows[6]
      }
    }}>
      <CardContent sx={{ 
        p: isMobile ? 2 : 3,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2
      }}>
        {/* Header Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 700,
              color: CONFIG.colors.textPrimary,
              display: "flex",
              alignItems: "center",
              gap: 1
            }}>
              <Avatar
                sx={{
                  bgcolor: colors.primary[500],
                  width: 32,
                  height: 32,
                  boxShadow: theme.shadows[2],
                }}
              >
                <AttachMoney fontSize="small" />
              </Avatar>
              Collection Efficiency Ratio
              <MuiTooltip title="Collected ÷ Due (Percentage of invoices successfully collected)">
                <InfoOutlined sx={{ 
                  fontSize: 18,
                  color: CONFIG.colors.textSecondary,
                  cursor: "pointer"
                }}/>
              </MuiTooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Percentage of invoices successfully collected 
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              size="small"
              onClick={toggleViewMode}
              startIcon={viewMode === "chart" ? <TableChart /> : <BarChartIcon />}
              sx={{
                textTransform: 'none',
                color: CONFIG.colors.textSecondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              {viewMode === "chart" ? "Table" : "Chart"}
            </Button>
            
            <Button
              size="small"
              onClick={exportToCSV}
              startIcon={<Download />}
              sx={{
                textTransform: 'none',
                color: CONFIG.colors.textSecondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Error/Sample Data Notice */}
        {error && (
          <Box>
            <Chip
              label={error}
              color="warning"
              size="small"
              variant="outlined"
              icon={<ErrorOutline fontSize="small" />}
              sx={{ 
                mb: 0.5,
                borderColor: colors.warning,
                color: colors.warning
              }}
            />
            {isUsingSampleData && (
              <Typography variant="caption" color="text.secondary">
                Sample data may not reflect actual business metrics
              </Typography>
            )}
          </Box>
        )}

        {/* Stats Summary */}
        <Box display="flex" flexDirection="column" gap={2}>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={120} />
          ) : (
            <Box display="grid" gridTemplateColumns={isMobile ? "1fr" : "repeat(4, 1fr)"} gap={2}>
              {/* Total Collected Card */}
              <Paper sx={{ 
                p: 2,
                borderRadius: 2,
                backgroundColor: CONFIG.colors.cardBg,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Total Collected
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  Rs.{totalCollected.toLocaleString()}
                </Typography>
                {collectedChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {collectedChange > 0 ? (
                      <ArrowUpward sx={{ fontSize: 14, color: collectedChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 14, color: collectedChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    )}
                    <Typography variant="caption" sx={{ 
                      color: collectedChange > 0 ? colors.greenAccent[500] : colors.redAccent[500],
                      ml: 0.5
                    }}>
                      {Math.abs(collectedChange).toFixed(1)}% change
                    </Typography>
                  </Box>
                )}
              </Paper>
              
              {/* Total Due Card */}
              <Paper sx={{ 
                p: 2,
                borderRadius: 2,
                backgroundColor: CONFIG.colors.cardBg,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Total Due
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  Rs.{totalDue.toLocaleString()}
                </Typography>
                {dueChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {dueChange > 0 ? (
                      <ArrowUpward sx={{ fontSize: 14, color: dueChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 14, color: dueChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    )}
                    <Typography variant="caption" sx={{ 
                      color: dueChange > 0 ? colors.greenAccent[500] : colors.redAccent[500],
                      ml: 0.5
                    }}>
                      {Math.abs(dueChange).toFixed(1)}% change
                    </Typography>
                  </Box>
                )}
              </Paper>
              
              {/* Efficiency Ratio Card */}
              <Paper sx={{ 
                p: 2,
                borderRadius: 2,
                backgroundColor: CONFIG.colors.cardBg,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Efficiency Ratio
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5} sx={{
                  color: efficiencyRatio > 80 ? colors.greenAccent[500] : 
                         efficiencyRatio > 60 ? colors.blueAccent[500] : colors.redAccent[500]
                }}>
                  {efficiencyRatio.toFixed(2)}%
                </Typography>
                {ratioChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {ratioChange > 0 ? (
                      <ArrowUpward sx={{ fontSize: 14, color: ratioChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 14, color: ratioChange > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    )}
                    <Typography variant="caption" sx={{ 
                      color: ratioChange > 0 ? colors.greenAccent[500] : colors.redAccent[500],
                      ml: 0.5
                    }}>
                      {Math.abs(ratioChange).toFixed(1)}% change
                    </Typography>
                  </Box>
                )}
              </Paper>
              
              {/* Monthly Growth Card */}
              <Paper sx={{ 
                p: 2,
                borderRadius: 2,
                backgroundColor: CONFIG.colors.cardBg,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Monthly Growth
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5} sx={{
                  color: monthlyGrowth > 0 ? colors.greenAccent[500] : colors.redAccent[500]
                }}>
                  {monthlyGrowth.toFixed(2)}%
                </Typography>
                {growthChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {monthlyGrowth > 0 ? (
                      <ArrowUpward sx={{ fontSize: 14, color: monthlyGrowth > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 14, color: monthlyGrowth > 0 ? colors.greenAccent[500] : colors.redAccent[500] }} />
                    )}
                    <Typography variant="caption" sx={{ 
                      color: monthlyGrowth > 0 ? colors.greenAccent[500] : colors.redAccent[500],
                      ml: 0.5
                    }}>
                      {growthChange.toFixed(1)}% change
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </Box>

        {/* Chart/Table Area */}
        <Box flex={1} minHeight={250} position="relative" mt={2}>
          {loading ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
            >
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : viewMode === "chart" ? (
            <Box 
              height="100%" 
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
              p={2}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`,
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={data}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: theme.palette.text.primary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <YAxis 
                    tick={{ fill: theme.palette.text.primary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <Tooltip 
                    content={<CustomTooltip />} 
                    cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="ratio" 
                    name="Efficiency Ratio (%)"
                    stroke={colors.blueAccent[500]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Collected (Rs.)</TableCell>
                    <TableCell align="right">Due (Rs.)</TableCell>
                    <TableCell align="right">Ratio (%)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.name}>
                      <TableCell component="th" scope="row">
                        {row.name}
                      </TableCell>
                      <TableCell align="right">Rs.{row.collected.toLocaleString()}</TableCell>
                      <TableCell align="right">Rs.{row.due.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ 
                        color: row.ratio > 80 ? colors.greenAccent[500] : 
                               row.ratio > 60 ? colors.blueAccent[500] : colors.redAccent[500],
                        fontWeight: 500
                      }}>
                        {row.ratio.toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 700 }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Rs.{totalCollected.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Rs.{totalDue.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 700,
                      color: efficiencyRatio > 80 ? colors.greenAccent[500] : 
                             efficiencyRatio > 60 ? colors.blueAccent[500] : colors.redAccent[500]
                    }}>
                      {efficiencyRatio.toFixed(2)}%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CollectionEfficiencyRatio;